import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { ToastContainer } from '../components/ToastContainer';
import { type ToastMessage, type ToastType } from '../components/Toast';

interface ToastContextType {
  showToast: (
    message: string,
    type?: ToastType,
    options?: Partial<ToastMessage>,
  ) => void;
  showSuccess: (message: string, options?: Partial<ToastMessage>) => void;
  showError: (message: string, options?: Partial<ToastMessage>) => void;
  showWarning: (message: string, options?: Partial<ToastMessage>) => void;
  showInfo: (message: string, options?: Partial<ToastMessage>) => void;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const showToast = useCallback(
    (
      message: string,
      type: ToastType = 'info',
      options: Partial<ToastMessage> = {},
    ) => {
      const id = generateId();
      const toast: ToastMessage = {
        id,
        type,
        message,
        duration: 5000,
        ...options,
      };

      setToasts((prev) => [...prev, toast]);
    },
    [generateId],
  );

  const showSuccess = useCallback(
    (message: string, options?: Partial<ToastMessage>) => {
      showToast(message, 'success', options);
    },
    [showToast],
  );

  const showError = useCallback(
    (message: string, options?: Partial<ToastMessage>) => {
      showToast(message, 'error', { duration: 7000, ...options });
    },
    [showToast],
  );

  const showWarning = useCallback(
    (message: string, options?: Partial<ToastMessage>) => {
      showToast(message, 'warning', options);
    },
    [showToast],
  );

  const showInfo = useCallback(
    (message: string, options?: Partial<ToastMessage>) => {
      showToast(message, 'info', options);
    },
    [showToast],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const value: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeToast,
    clearAllToasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
